import { AITrace } from "../models/aiTrace";
import axiosConfig from "./axiosConfig";

// Persisted portfolio metadata. Sidecar to DBLead.tags, keyed on the tag value.
export interface LeadPortfolioMetadata {
  tag: string;
  displayName: string | null;
  defaultTenantOrgId: string | null;
  defaultLandlordOrgId: string | null;
  rfiFileS3Key: string | null;
  taxonomyJson: Taxonomy | null;
  taxonomyMatchHash: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  createdByEmail: string | null;
}

// Per-site facts the AI model sees as input to the clustering step.
export interface TaxonomyInputSite {
  site_id: string;
  lead_id: string;
  site_name: string;
  building_use: string | null;
  floor_area_sqm: number | null;
  annual_kwh: number | null;
  // Site has its OWN uploaded half-hourly curve (can be a representative).
  has_hh_data: boolean;
  // Site has demand INFERRED from a representative via a prior assignment.
  has_inferred_hh_data: boolean;
  // When inferred: the representative site it was copied from + the group it was assigned into.
  inferred_from_site_id: string | null;
  taxonomy_group: string | null;
  voa_scat_code: string | null;
  voa_scat_description: string | null;
  voa_primary_description: string | null;
}

export interface TaxonomyInput {
  tag: string;
  sites: TaxonomyInputSite[];
  match_hash: string;
}

// A cluster proposed by the AI model. The FE may mutate `member_site_ids`
// and `proposed_representative_site_id` before submitting via assign-demand.
export interface TaxonomyCluster {
  id: string;
  label: string;
  rationale: string;
  member_site_ids: string[];
  proposed_representative_site_id: string | null;
  fallback_profile: {
    buildingProfile: string;
    profileShape: string;
    profileDays: string;
  } | null;
  coherence_warning: string | null;
}

export interface Taxonomy {
  clusters: TaxonomyCluster[];
  match_hash?: string;
  usage?: Record<string, number>;
}

export interface AssignDemandRequest {
  clusters: Array<{
    representativeSiteId: string | null;
    memberSiteIds: string[];
    // The taxonomy group label, persisted onto each receiver for provenance.
    label?: string | null;
  }>;
}

// The eventual assign result, read from the LRO operation result via /async_status.
export interface AssignDemandResponse {
  updatedSiteIds: string[];
  skipped: Array<{ site_id: string; reason: string }>;
}

export interface RemoveInferredDemandResponse {
  resetSiteIds: string[];
}

// Portfolio operations that write/recompute many sites (assign, remove-inferred, regenerate) run
// as tier-2 LROs keyed on the tag; the POST returns a handle to poll.
export interface PortfolioLroKickoff {
  operationKey: string;
  siteId: string;
  status: string;
}

export const getLeadPortfolioMetadata = async (
  authToken: string,
  tag: string
): Promise<LeadPortfolioMetadata | null> => {
  try {
    const { data } = await axiosConfig.get(
      `/leads/portfolio/${encodeURIComponent(tag)}/metadata`,
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    return data;
  } catch (e: unknown) {
    // 404 = no metadata yet for this tag, which is a normal state, not an error.
    const status = (e as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw e;
  }
};

export interface PutLeadPortfolioMetadataRequest {
  displayName?: string;
  defaultTenantOrgId?: string;
  defaultLandlordOrgId?: string;
  rfiFileS3Key?: string;
}

export const putLeadPortfolioMetadata = async (
  authToken: string,
  tag: string,
  body: PutLeadPortfolioMetadataRequest
): Promise<LeadPortfolioMetadata> => {
  const { data } = await axiosConfig.put(
    `/leads/portfolio/${encodeURIComponent(tag)}/metadata`,
    body,
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

export const getLeadPortfolioTaxonomyInput = async (
  authToken: string,
  tag: string
): Promise<TaxonomyInput> => {
  const { data } = await axiosConfig.get(
    `/leads/portfolio/${encodeURIComponent(tag)}/taxonomy-input`,
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

// Regenerate runs a Bedrock clustering call (slow at portfolio scale), so the POST kicks off a
// tier-2 LRO keyed on the tag and returns a handle to poll; the eventual result is a Taxonomy.
export const postLeadPortfolioTaxonomyRegenerate = async (
  authToken: string,
  tag: string
): Promise<PortfolioLroKickoff> => {
  const { data } = await axiosConfig.post(
    `/leads/portfolio/${encodeURIComponent(tag)}/taxonomy/regenerate`,
    {},
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

export const postLeadPortfolioAssignDemand = async (
  authToken: string,
  tag: string,
  body: AssignDemandRequest
): Promise<PortfolioLroKickoff> => {
  const { data } = await axiosConfig.post(
    `/leads/portfolio/${encodeURIComponent(tag)}/assign-demand`,
    body,
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

// Reset every portfolio site that had demand INFERRED via assignment back to no inferred HH.
export const postLeadPortfolioRemoveInferredDemand = async (
  authToken: string,
  tag: string
): Promise<PortfolioLroKickoff> => {
  const { data } = await axiosConfig.post(
    `/leads/portfolio/${encodeURIComponent(tag)}/remove-inferred-demand`,
    {},
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

// Persist the operator's edited taxonomy (add/remove/move clusters) as the cached taxonomy.
export const putLeadPortfolioTaxonomy = async (
  authToken: string,
  tag: string,
  clusters: TaxonomyCluster[]
): Promise<{ taxonomyJson: Taxonomy; taxonomyMatchHash: string }> => {
  const { data } = await axiosConfig.put(
    `/leads/portfolio/${encodeURIComponent(tag)}/taxonomy`,
    { clusters },
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

// Fetch the complete AI trace from the last taxonomy generation for this portfolio.
// Returns null when no trace is available (e.g. a taxonomy copied down from prod in a
// non-prod env - traces aren't copied).
export const getLeadPortfolioTaxonomyTrace = async (
  authToken: string,
  tag: string
): Promise<AITrace | null> => {
  const { data } = await axiosConfig.get(
    `/leads/portfolio/${encodeURIComponent(tag)}/taxonomy-trace`,
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data?.trace ?? null;
};
